/**
 * storage.js
 * Abstraction pour localStorage - Persistance et CRUD des données
 */

const Storage = (() => {
  const STORAGE_KEY = 'timeTrackingData';
  const VERSION = '1.0';

  /**
   * Initialiser les données (appel au premier chargement)
   */
  function init() {
    if (!localStorage.getItem(STORAGE_KEY)) {
      const initialData = {
        metadata: {
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          version: VERSION
        },
        structures: {},
        entries: []
      };
      setData(initialData);
    }
  }

  /**
   * Récupérer toutes les données
   */
  function getData() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        init();
        return getData();
      }
      return JSON.parse(data);
    } catch (error) {
      console.error('Erreur lecture localStorage:', error);
      return null;
    }
  }

  /**
   * Sauvegarder toutes les données
   */
  function setData(data) {
    try {
      data.metadata = data.metadata || {};
      data.metadata.lastUpdated = new Date().toISOString();
      data.metadata.version = VERSION;

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      checkStorageQuota();
      return true;
    } catch (error) {
      console.error('Erreur écriture localStorage:', error);
      return false;
    }
  }

  /**
   * Ajouter des entrées (bulk import depuis CSV)
   * Remplace les entrées existantes pour les mêmes structures et la même plage de dates,
   * garantissant qu'un ré-import du même fichier donne toujours le même résultat.
   */
  function addEntries(entries) {
    if (!entries || entries.length === 0) return false;

    const data = getData();
    if (!data) return false;

    data.entries = data.entries || [];

    // Déterminer la plage de dates et les structures couvertes par les nouvelles entrées
    const dates = entries.map(e => e.date).sort();
    const minDate = dates[0];
    const maxDate = dates[dates.length - 1];
    const structures = new Set(entries.map(e => e.structure).filter(Boolean));

    // Supprimer les entrées existantes pour ces structures dans cette plage de dates
    data.entries = data.entries.filter(e =>
      !e.structure ||
      !structures.has(e.structure) ||
      e.date < minDate ||
      e.date > maxDate
    );

    data.entries.push(...entries);
    return setData(data);
  }

  /**
   * Récupérer entrées par structure et plage de dates
   */
  function getEntriesByStructure(structure, startDate, endDate) {
    const data = getData();
    if (!data || !data.entries) return [];

    return data.entries.filter(entry => {
      const entryDate = new Date(entry.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include full end day

      const matchesStructure = !structure || entry.structure === structure;
      return matchesStructure &&
             entryDate >= start &&
             entryDate <= end;
    });
  }

  /**
   * Récupérer toutes les entrées (optionnel: filtrer par structure)
   */
  function getAllEntries(structure = null) {
    const data = getData();
    if (!data || !data.entries) return [];

    if (!structure) return data.entries;

    return data.entries.filter(entry => entry.structure === structure);
  }

  /**
   * Supprimer une entrée par ID
   */
  function deleteEntry(entryId) {
    const data = getData();
    if (!data || !data.entries) return false;

    data.entries = data.entries.filter(entry => entry.id !== entryId);
    return setData(data);
  }

  /**
   * Supprimer toutes les entrées d'une structure
   */
  function deleteEntriesByStructure(structure) {
    const data = getData();
    if (!data || !data.entries) return false;

    data.entries = data.entries.filter(entry => entry.structure !== structure);
    return setData(data);
  }

  /**
   * Supprimer TOUTES les données (confirmation recommandée)
   */
  function clear() {
    if (window.confirm('⚠️ Êtes-vous sûr ? Cela supprimera TOUTES les données.')) {
      localStorage.removeItem(STORAGE_KEY);
      init();
      return true;
    }
    return false;
  }

  /**
   * Exporter les données en JSON (téléchargement)
   */
  function exportJSON(structure = null) {
    const data = getData();
    if (!data) return null;

    const toExport = {
      metadata: data.metadata,
      entries: structure ? getEntriesByStructure(structure, '1900-01-01', '2099-12-31') : data.entries
    };

    const json = JSON.stringify(toExport, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `time-tracking-${structure || 'all'}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);

    return toExport;
  }

  /**
   * Importer des données depuis JSON
   */
  function importJSON(jsonString) {
    try {
      const imported = JSON.parse(jsonString);
      const current = getData();

      if (imported.entries && Array.isArray(imported.entries)) {
        current.entries.push(...imported.entries);
        return setData(current);
      }
      return false;
    } catch (error) {
      console.error('Erreur import JSON:', error);
      return false;
    }
  }

  /**
   * Vérifier l'espace disque utilisé et avertir si >80%
   */
  function checkStorageQuota() {
    const data = localStorage.getItem(STORAGE_KEY);
    const used = new Blob([data]).size;
    const limit = 5 * 1024 * 1024; // ~5MB
    const percentage = (used / limit) * 100;

    if (percentage > 80) {
      console.warn(`⚠️ localStorage utilisé à ${percentage.toFixed(1)}%`);
    }

    return {
      used,
      limit,
      percentage
    };
  }

  /**
   * Obtenir des stats sur les données stockées
   */
  function getStats() {
    const data = getData();
    if (!data) return null;

    const quota = checkStorageQuota();
    const entries = data.entries || [];
    const structures = Object.keys(data.structures || {});

    return {
      totalEntries: entries.length,
      structures: structures,
      storageUsed: quota.percentage.toFixed(1) + '%',
      lastUpdated: data.metadata?.lastUpdated
    };
  }

  // Initialiser au chargement
  init();

  return {
    init,
    getData,
    setData,
    addEntries,
    getEntriesByStructure,
    getAllEntries,
    deleteEntry,
    deleteEntriesByStructure,
    clear,
    exportJSON,
    importJSON,
    checkStorageQuota,
    getStats
  };
})();

export default Storage;
